/**
 * FairSkeleton / FairImage tokens (FE-005).
 * Shimmer visuals live in `fair-skeleton.css`; this file only carries the
 * shape contract shared by the primitives and their consumers.
 */

/** `text` = copy line · `media` = image box · `circle` = avatar / swatch. */
export type FairSkeletonVariant = 'text' | 'media' | 'circle'

export const FAIR_SKELETON_VARIANTS: readonly FairSkeletonVariant[] = [
  'text',
  'media',
  'circle',
]

/** Default height of a copy line — matches the 14px body line box. */
export const FAIR_SKELETON_TEXT_HEIGHT = 14

/**
 * FR monogram inside an image skeleton, as a share of the shorter box side.
 * Capped so the mark stays a hint on large media and readable on thumbnails.
 */
export const FAIR_SKELETON_MARK_RATIO = 0.28
export const FAIR_SKELETON_MARK_MIN_PX = 24
export const FAIR_SKELETON_MARK_MAX_PX = 72

export function normalizeFairSkeletonVariant(
  variant: string | undefined,
): FairSkeletonVariant {
  return FAIR_SKELETON_VARIANTS.includes(variant as FairSkeletonVariant)
    ? (variant as FairSkeletonVariant)
    : 'text'
}

/** CSS length from a number (px) or a ready string (`50%`, `12rem`). */
export function toFairSkeletonLength(
  value: number | string | undefined,
): string | undefined {
  if (value == null) return undefined
  return typeof value === 'number' ? `${value}px` : value
}

/** Mark size for a media skeleton of the given box, in px. */
export function resolveFairSkeletonMarkSize(shorterSidePx: number): number {
  const raw = shorterSidePx * FAIR_SKELETON_MARK_RATIO
  return Math.round(
    Math.min(FAIR_SKELETON_MARK_MAX_PX, Math.max(FAIR_SKELETON_MARK_MIN_PX, raw)),
  )
}
